import { CommonModule } from '@angular/common';
import { Component, computed, Inject, OnInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardActions, MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subject } from 'rxjs';
import { SchedulingSitesResponse } from '../../../../interfaces/siteType';
import { TPMInputData } from '../../../../interfaces/streamrecurringLoad';
import { FeedstockService } from '../../../../services/feedstockservice';
import { LookUpService } from '../../../../services/lookupservice';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FieldConfiguration, RentalType, TotePmGridColumns, TotePriceMatrix, ToteRentalType, ToterPriceMatrix } from '../../../../interfaces/pricematrixmodel';
import { MatMenuModule } from '@angular/material/menu';
import { AddeditRentaltypeComponent } from './addedit-rentaltype/addedit-rentaltype.component';
import { AlertDialogComponent } from '../../../shared/components/alert-dialog/alert-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { add } from 'date-fns';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { formatDateOnly, numericInputLimit } from '../../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
@Component({
  selector: 'app-addedit-totepricematrix',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule, // FIX: mat-form-field error
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatDialogModule,
    DragDropModule
  ],
  templateUrl: './addedit-totepricematrix.component.html',
  styleUrl: './addedit-totepricematrix.component.scss'
})
export class AddeditTotepricematrixComponent implements OnInit {
  totePmForm!: FormGroup;
  isWalmartGenerator: boolean = false;
  visibleFields: string[] = [];
  canSave: boolean = false;
  // State signals
  attemptSave = signal(false);
  isSaving = signal(false);
  dateError = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  streamID = signal<number | null>(null);
  genId = signal<number | null>(null);
  tpmId = signal<number | null>(null);
  disposalSites = signal<SchedulingSitesResponse[]>([]);
  filtereddisposalSites = signal<SchedulingSitesResponse[]>([]);
  task: string = "";
  rentalDisplayedColumns: string[] = ['actions', 'rentalTypeName', 'fee', 'quantity', 'rentalSize', 'startDate', 'endDate'];
  rentalDataSource = new MatTableDataSource<any>();
  dynamicColumns = signal<TotePmGridColumns[]>([]);
  displayValue = signal<string>('');
  showDeleteRentalType: boolean = false;
  private destroy$ = new Subject<void>();

  fieldVisibilityMap = computed(() => {
    const map = new Map<string, boolean>();
    this.dynamicColumns().forEach(col => {
      map.set(col.columnDef.toLowerCase(), col.visible);
    });
    return map;
  });

  constructor(private fsService: FeedstockService,
    private lookup: LookUpService,
    public dialogRef: MatDialogRef<AddeditTotepricematrixComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TPMInputData,
    private tpmForm: FormBuilder, private dialog: MatDialog) {
    console.log("TPM Dialog data:", data);
    this.streamID.set(data.streamId);
    this.genId.set(data.genId);
    if (this.genId() === 267 || this.genId() === 266) { // Tote Price Matrix Tab
      this.isWalmartGenerator = true;
    }
    this.task = data.task;
    this.canSave = data.canSave;
    this.showDeleteRentalType = data.showGridDeleteButton;
    if (data.pmInputData) {
      this.isEditMode.set(true);
    }
  }

  ngOnInit(): void {
    this._initializeForm();
    this.loadFieldConfigurations();
    if (this.task === 'Duplicate') {
      this.isEditMode.set(false);
    }
  }

  private _initializeForm(): void {
    this.totePmForm = this.tpmForm.group({
      disposalSite: [null],
      monthlyCharges: [null],
      effectiveStartDate: [new Date()],
      effectiveEndDate: [null],

      // Walmart Specific Fields
      extraPickup: [null],
      perPickupFee: [null],
      contaminationFee: [null],
      emergencyPickupFee: [null],
      leakingBin: [null],
      damagedBin: [null],
      landfillHaul: [null],
      newKey: [null],


      fuelSurcharge: [null, Validators.max(100)],
      lockBeforeDelivery: [null],
      lockAfterDelivery: [null],
      relocationFee: [null],
      startupFee: [null],

      // Common Fields
      eis: [null, Validators.max(100)],
      toteReplacement: [null],
      removalRedeliveryFee: [null],
      additionalPickupFeeForSite: [null],

      rentalTypes: this.tpmForm.array([], [this.rentalTypeRequiredValidator()])
    });
    //this.applyConditionalValidators();
  }

  loadDropdownData() {
    this.fsService.getSchedulingSites(5, 5).subscribe({
      next: async (response) => {
        this.disposalSites.set(response);
        this.filtereddisposalSites.set(response);
        if (this.isEditMode() || this.task === 'Duplicate') {
          this.populateFormData(this.data.pmInputData);
        }
      }
    });
  }

  loadFieldConfigurations(): void {
    this.fsService.getTPMFieldConfig(this.data.genId).subscribe({
      next: (res: FieldConfiguration[]) => {
        console.log("tpm config data", res);
        const dynamicCols: TotePmGridColumns[] = (res || [])
          .filter(config => config.visible)
          .map(config => ({
            columnDef: config.fieldName,
            header: config.label,
            visible: config.visible
          }));

        this.dynamicColumns.set(dynamicCols);
        this.applyConditionalValidators();
        this.loadDropdownData();
      }
    });
  }

  populateFormData(pmData: TotePriceMatrix | null): void {
    if (!pmData) return;
    const currentId = this.task === 'Duplicate' ? 0 : pmData.Id;
    const rawIds = pmData.DisposalSite ? pmData.DisposalSite.split(',') : [];
    const availableSids = this.disposalSites().map(site => site.sid);
    const disposalSiteIdsArray = rawIds.filter(id => availableSids.includes(id));

    this.tpmId.set(currentId || 0);
    this.totePmForm.patchValue({
      id: currentId,
      disposalSite: disposalSiteIdsArray,
      monthlyCharges: pmData.MonthlyCharges ?? null,

      effectiveStartDate: pmData.Effective_StartDate ?? null,
      effectiveEndDate: pmData.Effective_EndDate ?? null,
      extraPickup: pmData.ExtraPickup ?? null,

      perPickupFee: (pmData as any).PerPickupFee ?? null,
      contaminationFee: (pmData as any).ContaminationFee ?? null,
      emergencyPickupFee: (pmData as any).EmergencyPickupFee ?? null,
      leakingBin: (pmData as any).LeakingBin ?? null,
      damagedBin: (pmData as any).DamagedBin ?? null,
      landfillHaul: (pmData as any).LandfillHaul ?? null,
      newKey: (pmData as any).NewKey ?? null,

      fuelSurcharge: pmData.FuelSurcharge ?? null,
      lockBeforeDelivery: pmData.LockBeforeDelivery ?? null,
      lockAfterDelivery: pmData.LockAfterDelivery ?? null,
      relocationFee: pmData.RelocationFee ?? null,
      startupFee: pmData.StartupFee ?? null,

      // Common Fields
      eis: pmData.EIS ?? null,
      toteReplacement: pmData.ToteReplacement ?? null,
      removalRedeliveryFee: pmData.RemovalRedeliveryFee ?? null,
      additionalPickupFeeForSite: pmData.AdditionalPickupFeeForSite ?? null,
    });

    this.rentalDataSource.data = pmData.ToteRentalTypes || [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isFieldVisible(fieldName: string): boolean {
    return this.fieldVisibilityMap().get(fieldName.toLowerCase()) ?? false;
  }

  applyConditionalValidators(): void {
    const requiredFieldMap: { controlName: string, visibilityKey: string }[] = [
      { controlName: 'disposalSite', visibilityKey: 'disposalSiteNames' },
      { controlName: 'effectiveStartDate', visibilityKey: 'effective_StartDate' },
    ];

    requiredFieldMap.forEach(field => {
      const control = this.totePmForm.get(field.controlName);
      if (this.isFieldVisible(field.visibilityKey) && control) {
        control.setValidators(Validators.required);
        control.updateValueAndValidity();
      }
    });

    const monthlyCharges = this.totePmForm.get('monthlyCharges');
    if (monthlyCharges) {
      const monthlyChargesRequired = !this.isWalmartGenerator && this.isFieldVisible('monthlyCharges');
      monthlyChargesRequired
        ? monthlyCharges.setValidators(Validators.required)
        : monthlyCharges.clearValidators();
      monthlyCharges.updateValueAndValidity();
    }
  }

  roundToFourDecimals(controlName: string): void {
    const control = this.totePmForm.get(controlName);

    if (control && (control.value !== null || control.value !== undefined)) {
      const valueAsString = String(control.value).trim();
      const numericValue = parseFloat(valueAsString);

      if (!isNaN(numericValue)) {
        const roundedValue = parseFloat(numericValue.toFixed(4));
        if (numericValue.toFixed(4) !== roundedValue.toFixed(4)) {
          control.setValue(roundedValue, { emitEvent: true });
        }
      }
    }
  }

  rentalTypeRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;

      const disposalSiteFilled = !!this.totePmForm?.get('disposalSite')?.value;
      const effectiveStartDateFilled = !!this.totePmForm?.get('effectiveStartDate')?.value;

      const formReady = disposalSiteFilled && effectiveStartDateFilled;

      if (formReady && formArray.length === 0) {
        return { rentalTypeRequired: true };
      }
      return null;
    };
  }

  // Getter for the FormArray
  get rentalTypes() {
    return this.totePmForm.get('rentalTypes') as FormArray;
  }

  addorEditRentalType(row: ToteRentalType | null, isEdit: boolean): void {
    {
      const dialogRef = this.dialog.open(AddeditRentaltypeComponent, {
        width: '650px',
        maxHeight: '90vh',
        disableClose: true,
        data: row ? { ...row, task: 'Edit', priceMatrixId: this.tpmId() || 0 } : { task: 'Add', priceMatrixId: this.tpmId() || 0 }
      });

      dialogRef.afterClosed().subscribe((result: ToteRentalType) => {
        if (result) {
          const currentData = this.rentalDataSource.data;
          if (row) {
            const index = currentData.findIndex(item => item.id === row.id);
            if (index > -1) {
              currentData[index] = result;
            }
          } else {
            currentData.push(result);
          }
          this.rentalDataSource.data = [...currentData];
        }
      });
    }
  }

  deleteRentalType(row: ToteRentalType) {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete Rental Type ${row.rentalTypeName}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true,
    }).afterClosed().subscribe(result => {
      if (result) {
        if (row.id !== null && row.id !== undefined) {
          this.rentalDataSource.data = this.rentalDataSource.data.filter(item => item.id !== row.id);
        }
      }
    });
  }

  // Convert form data keys to PascalCase for API consistency
  prepareDataForApi(formData: any): any {
    const apiData: any = {};
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        let pascalKey = key.charAt(0).toUpperCase() + key.slice(1);

        if (key === 'eis') {
          pascalKey = 'EIS';
        }
        if (key.toLowerCase() === 'disposalsite' && Array.isArray(formData[key])) {
          apiData[pascalKey] = formData[key].join(',');
          continue;
        }
        if (key.toLowerCase() === 'disposalsites') {
          apiData[pascalKey] = formData['disposalSite'].join(',');
          continue;
        }
        if (key.toLowerCase() === 'effectivestartdate') {
          apiData[pascalKey] = formatDateOnly(formData['effectiveStartDate']);
          continue;
        }
        if (key.toLowerCase() === 'effectiveenddate') {
          apiData[pascalKey] = formatDateOnly(formData['effectiveEndDate']);
          continue;
        }
        apiData[pascalKey] = formData[key];
      }
    }
    return apiData;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.ctrlKey && event.metaKey) { return; }
    if (!/[0-9\/]/.test(event.key) && event.key.length === 1) {
      event.preventDefault();
    }
  }
  dateformat(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (!value) return;
    const parts = value.split('/');
    if (parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if (month.length === 1) month = '0' + month;
    if (day.length === 1) day = '0' + day;
    if (year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }

  onSave(): void {
    this.totePmForm.markAllAsTouched();
    if (this.totePmForm.valid) {

      const formData = this.totePmForm.value;
      let startDate: Date | null = formData.effectiveStartDate;
      let endDate: Date | null = formData.effectiveEndDate;
      if (startDate && endDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
  
        const end = new Date(endDate);
        end.setHours(0, 0, 0, 0);
  
        if (start.getTime() > end.getTime()) {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Alert',
              message: 'End date should be after Start date.'
            },
            disableClose: true,
            panelClass: 'delete-confirmation-dialog-nowrap'
          });
          this.isSaving.set(false);
          return;
        }
      }

      const dataToSend = this.prepareDataForApi(formData);
      dataToSend.id = this.tpmId() || 0;
      dataToSend.streamID = this.data.streamId;
      dataToSend.genID = this.data.genId;
      dataToSend.DisposalSites = formData.disposalSite.join(',');
      //dataToSend.Effective_StartDate = formData.effectiveStartDate ? formatDateOnly(formData.effectiveStartDate) : null;
      //dataToSend.Effective_EndDate = formData.effectiveEndDate ? formatDateOnly(formData.effectiveEndDate) : null;
      const rentalTypesRequest: ToteRentalType[] =
        this.rentalDataSource.data.map(item => {
          return {
            id: item.id,
            rentalTypeName: item.rentalTypeName,
            fee: item.fee,
            quantity: item.quantity,
            size: item.size,
            startDate: item.startDate ? formatDateOnly(item.startDate) : null,
            endDate: item.endDate ? formatDateOnly(item.endDate) : null,
            priceMatrixId: this.data.pmInputData ? this.data.pmInputData.Id : 0,
            task: this.isEditMode() ? 'Edit' : 'Add',
            rentalTypeId: item.rentalTypeId,
            rentalSize: item.rentalSize
          };
        });

      dataToSend.toteRentalTypes = rentalTypesRequest;

      console.log('Form is valid. Preparing data...', dataToSend);
      this.isSaving.set(true);

      if (rentalTypesRequest.length === 0) {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Alert',
            message: 'Please enter at least one Rental Type.'
          },
          panelClass: 'delete-confirmation-dialog'
        });
        this.isSaving.set(false);
        return;
      }
      this.fsService.saveTPM(dataToSend).subscribe({
        next: (response) => {
          console.log('Save successful:', response);
          this.isSaving.set(false);
          this.dialogRef.close(true);
        }
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    val = numericInputLimit(event, maxValue, maxDecimals);

    this.displayValue.set(val);
    input.value = val;

    // 6. Parsing for the backing variables    
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.totePmForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.totePmForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.totePmForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
          input.value = val;
      }
    });
  }

  filterSites(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    const selectedIds = this.totePmForm.get('disposalSite')?.value || [];
    if (!searchTerm) {
      this.filtereddisposalSites.set([...this.disposalSites()]);
    } else {
      const filtered = this.disposalSites().filter(
        g => g.siteName.toLowerCase().includes(searchTerm) || selectedIds.includes(g.sid)
      );
      this.filtereddisposalSites.set(filtered);
    }
  }
}
